import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useRateLimit } from "@/hooks/use-rate-limit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Logo } from "@/components/Logo";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowLeft } from "lucide-react";
import { z } from "zod";
import loginBg from "@/assets/login-bg.jpg";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";

const resetEmailSchema = z.object({
  email: z.string()
    .trim()
    .email("Please enter a valid email address")
    .max(255, "Email is too long"),
});

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isResetting, setIsResetting] = useState(false);
  const [isGoogleOAuthUser, setIsGoogleOAuthUser] = useState(false);
  const [showGoogleLoginMessage, setShowGoogleLoginMessage] = useState(false);
  const [rateLimitMessage, setRateLimitMessage] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [resetCaptchaToken, setResetCaptchaToken] = useState<string | null>(null);
  const [showNewAccountDialog, setShowNewAccountDialog] = useState(false);
  const [isCheckingAccount, setIsCheckingAccount] = useState(false);
  const turnstileRef = useRef<TurnstileInstance>(null);
  const resetTurnstileRef = useRef<TurnstileInstance>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { checkRateLimit, isLimited, formatRetryTime } = useRateLimit();

  useEffect(() => {
    // Fetch the logo from any user's profile (public branding)
    const fetchLogo = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('north_logo_url')
        .not('north_logo_url', 'is', null)
        .limit(1)
        .maybeSingle();
      
      if (data?.north_logo_url) {
        setLogoUrl(data.north_logo_url);
      }
    };
    fetchLogo();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setRateLimitMessage(null);

    try {
      // Check captcha token (only when Turnstile is configured)
      if (import.meta.env.VITE_TURNSTILE_SITE_KEY && !captchaToken) {
        toast({
          title: "Verification required",
          description: "Please complete the security check",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      // Check rate limit before attempting login
      const rateLimitResult = await checkRateLimit("login", email.toLowerCase().trim());
      if (!rateLimitResult.allowed) {
        setRateLimitMessage(rateLimitResult.message || "Too many login attempts. Please try again later.");
        setIsLoading(false);
        return;
      }

      const { error, data } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: {
          captchaToken,
        },
      });

      if (error) throw error;

      // Wait for session to be fully established
      if (data.session) {
        toast({
          title: "Success",
          description: "Logged in successfully",
        });

        // Small delay to ensure session is persisted
        setTimeout(() => {
          navigate("/dashboard");
        }, 100);
      }
    } catch (error: any) {
      // Reset turnstile on error
      turnstileRef.current?.reset();
      setCaptchaToken(null);

      // Handle specific Supabase auth errors with user-friendly messages
      let errorMessage = error.message || "Failed to log in";

      // Check for Google OAuth user trying to use password login
      if (errorMessage.includes("missing email or phone") || errorMessage.includes("Invalid login credentials")) {
        // Check if this might be a Google OAuth user
        const { data: profile } = await supabase
          .from('profiles')
          .select('auth_provider')
          .eq('email', email.toLowerCase().trim())
          .maybeSingle();

        if (profile?.auth_provider === 'google') {
          // Show inline message instead of toast
          setShowGoogleLoginMessage(true);
          setIsLoading(false);
          return;
        } else {
          errorMessage = "Invalid email or password. Please check your credentials and try again.";
        }
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent bubbling to parent login form

    // Validate email
    const validation = resetEmailSchema.safeParse({ email: resetEmail });
    if (!validation.success) {
      toast({
        title: "Invalid email",
        description: validation.error.errors[0].message,
        variant: "destructive",
      });
      return;
    }

    setIsResetting(true);

    try {
      // Check rate limit before attempting password reset
      const rateLimitResult = await checkRateLimit("reset", resetEmail.toLowerCase().trim());
      if (!rateLimitResult.allowed) {
        toast({
          title: "Too many requests",
          description: rateLimitResult.message || "Please wait before requesting another password reset.",
          variant: "destructive",
        });
        setIsResetting(false);
        return;
      }

      // Check if this email belongs to a Google OAuth user
      // We query profiles to see if the user exists and what their auth provider is
      // Note: If auth_provider isn't set correctly in profiles, we'll still allow the reset
      // since Supabase supports multiple auth methods per user
      // Use case-insensitive email matching since emails might be stored with different casing
      const normalizedEmail = resetEmail.toLowerCase().trim();
      const { data: profile } = await supabase
        .from('profiles')
        .select('email, auth_provider, user_id')
        .ilike('email', normalizedEmail)
        .maybeSingle();

      // If profile exists and auth_provider is explicitly set to 'google', block password reset
      // Google OAuth users should use Google sign-in, not password reset
      if (profile?.auth_provider === 'google') {
        setIsGoogleOAuthUser(true);
        setIsResetting(false);
        return;
      }
      
      // If profile exists but auth_provider is not set or is 'email', allow password reset
      // This handles cases where the migration didn't run or the field wasn't set correctly

      // For email/password users (or unknown emails for security), send reset email
      // Include captchaToken if available (Supabase may require it if captcha is enabled)
      const resetOptions: { redirectTo: string; captchaToken?: string } = {
        redirectTo: `${window.location.origin}/reset-password`,
      };
      
      // Only include captchaToken if we have one (optional for password reset)
      if (resetCaptchaToken) {
        resetOptions.captchaToken = resetCaptchaToken;
      }

      const { error } = await supabase.auth.resetPasswordForEmail(
        resetEmail,
        resetOptions
      );

      if (error) {
        // Reset turnstile on error
        resetTurnstileRef.current?.reset();
        setResetCaptchaToken(null);
        
        // Handle captcha verification errors specifically
        if (error.message?.toLowerCase().includes('captcha') || 
            error.message?.toLowerCase().includes('verification')) {
          toast({
            title: "Verification Failed",
            description: "The security check failed. Please complete the verification again and try once more.",
            variant: "destructive",
          });
          setIsResetting(false);
          return;
        }
        
        throw error;
      }

      toast({
        title: "Check your email",
        description: "If an account exists with this email, we've sent a password reset link.",
      });

      setIsResetDialogOpen(false);
      setResetEmail("");
      setResetCaptchaToken(null);
      resetTurnstileRef.current?.reset();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send reset email",
        variant: "destructive",
      });
    } finally {
      setIsResetting(false);
    }
  };

  const handleGoogleOAuthClick = async () => {
    setIsCheckingAccount(true);

    // Since we can't know the Google email before OAuth, we'll proceed directly
    // but show a confirmation dialog that this is the login page
    // If they don't have an account, they'll see a message after OAuth
    setShowNewAccountDialog(true);
    setIsCheckingAccount(false);
  };

  const proceedWithGoogleOAuth = async () => {
    setShowNewAccountDialog(false);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        // Redirect to select-plan - it will check for subscription and redirect to dashboard if user has one
        redirectTo: `${window.location.origin}/select-plan`
      }
    });
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const redirectToSignup = () => {
    setShowNewAccountDialog(false);
    navigate('/signup');
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${loginBg})` }}
      />
      {/* Black overlay */}
      <div className="absolute inset-0 bg-black/60" />

      {/* Back to home link */}
      <Link
        to="/"
        className="absolute top-8 left-8 z-20 flex items-center gap-2 text-white hover:text-white/80 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        <span>Back to home</span>
      </Link>

      {/* Content */}
      <Card className="w-full max-w-md relative z-10">
        <CardHeader className="space-y-8">
          <div className="flex justify-center pt-2">
            <Logo customLogoUrl={logoUrl} size="lg" />
          </div>
          <CardTitle className="text-2xl text-center">Welcome back</CardTitle>
          <CardDescription className="text-center">
            Sign in to your account to continue
          </CardDescription>
        </CardHeader>
        <CardContent>
          {rateLimitMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm font-medium text-red-800 mb-1">Too Many Attempts</p>
              <p className="text-sm text-red-800">{rateLimitMessage}</p>
            </div>
          )}
          {showGoogleLoginMessage && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm font-medium text-blue-800 mb-2">Google Sign-In Account</p>
              <p className="text-sm text-blue-800">
                This account uses Google Sign-In for authentication. Please use the <strong>"Continue with Google"</strong> button below to access your account.
              </p>
            </div>
          )}
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setShowGoogleLoginMessage(false); // Clear message when email changes
                  setRateLimitMessage(null); // Clear rate limit message
                }}
                required
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Dialog open={isResetDialogOpen} onOpenChange={(open) => {
                  setIsResetDialogOpen(open);
                  if (!open) {
                    // Reset state when dialog closes
                    setIsGoogleOAuthUser(false);
                    setResetEmail("");
                  }
                }}>
                  <DialogTrigger asChild>
                    <button
                      type="button"
                      className="text-sm text-primary hover:underline"
                      disabled={isLoading}
                    >
                      Forgot password?
                    </button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    {isGoogleOAuthUser ? (
                      <>
                        <DialogHeader>
                          <DialogTitle>Google Sign-In Account</DialogTitle>
                          <DialogDescription>
                            This email is linked to Google Sign-In.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-sm text-blue-800">
                              Your account uses Google Sign-In for authentication. Please use the <strong>"Continue with Google"</strong> button on the login page to access your account.
                            </p>
                            <p className="text-sm text-blue-800 mt-3">
                              If you've forgotten your Google password, you can reset it at{" "}
                              <a
                                href="https://accounts.google.com/signin/recovery"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium underline hover:text-blue-900"
                              >
                                accounts.google.com
                              </a>
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="gold"
                            className="w-full"
                            onClick={() => {
                              setIsResetDialogOpen(false);
                              setIsGoogleOAuthUser(false);
                              setResetEmail("");
                            }}
                          >
                            Got it
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <DialogHeader>
                          <DialogTitle>Reset your password</DialogTitle>
                          <DialogDescription>
                            Enter your email address and we'll send you a link to reset your password.
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleForgotPassword} className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="reset-email">Email</Label>
                            <Input
                              id="reset-email"
                              type="email"
                              placeholder="you@example.com"
                              value={resetEmail}
                              onChange={(e) => setResetEmail(e.target.value)}
                              required
                              disabled={isResetting}
                            />
                          </div>
                          {/* Turnstile CAPTCHA for password reset */}
                          {import.meta.env.VITE_TURNSTILE_SITE_KEY && (
                            <div className="flex justify-center">
                              <Turnstile
                                ref={resetTurnstileRef}
                                siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                                onSuccess={setResetCaptchaToken}
                                onError={() => {
                                  setResetCaptchaToken(null);
                                  toast({
                                    title: "Verification Error",
                                    description: "Security verification failed. Please try again.",
                                    variant: "destructive",
                                  });
                                }}
                                onExpire={() => {
                                  setResetCaptchaToken(null);
                                  toast({
                                    title: "Verification Expired",
                                    description: "The security check expired. Please complete it again.",
                                    variant: "destructive",
                                  });
                                }}
                                options={{
                                  theme: "light",
                                  size: "normal",
                                }}
                              />
                            </div>
                          )}
                          <div className="flex gap-3">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                setIsResetDialogOpen(false);
                                setResetEmail("");
                                setResetCaptchaToken(null);
                                resetTurnstileRef.current?.reset();
                              }}
                              className="flex-1"
                              disabled={isResetting}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                              variant="gold"
                              className="flex-1"
                              disabled={isResetting || (import.meta.env.VITE_TURNSTILE_SITE_KEY && !resetCaptchaToken)}
                            >
                              {isResetting ? "Sending..." : "Send reset link"}
                            </Button>
                          </div>
                        </form>
                      </>
                    )}
                  </DialogContent>
                </Dialog>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            {/* Turnstile CAPTCHA */}
            {import.meta.env.VITE_TURNSTILE_SITE_KEY && (
              <div className="flex justify-center">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                  onSuccess={setCaptchaToken}
                  onError={() => setCaptchaToken(null)}
                  onExpire={() => setCaptchaToken(null)}
                  options={{
                    theme: "light",
                    size: "normal",
                  }}
                />
              </div>
            )}

            <Button type="submit" variant="gold" className="w-full" disabled={isLoading || (!!import.meta.env.VITE_TURNSTILE_SITE_KEY && !captchaToken)}>
              {isLoading ? "Signing in..." : "Sign in"}
            </Button>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleGoogleOAuthClick}
              disabled={isLoading || isCheckingAccount}
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {isCheckingAccount ? "Checking..." : "Continue with Google"}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              Don't have an account?{" "}
              <Link to="/signup" className="text-primary hover:underline">
                Sign up
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>

      {/* New Account Confirmation Dialog */}
      <Dialog open={showNewAccountDialog} onOpenChange={setShowNewAccountDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Sign In with Google</DialogTitle>
            <DialogDescription>
              You're about to sign in with your Google account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm font-medium text-amber-800 mb-2">Already have an account?</p>
              <p className="text-sm text-amber-800">
                If you have an existing account with this Google email, you'll be signed in automatically.
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm font-medium text-blue-800 mb-2">New to North Reports?</p>
              <p className="text-sm text-blue-800">
                If we don't find an account, we'll create one for you and guide you through selecting a plan to start creating beautiful seller reports.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNewAccountDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="gold"
                onClick={proceedWithGoogleOAuth}
                className="flex-1"
              >
                Continue with Google
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
